/** 게임뷰 interior 기준 오버레이 배치 rect. */
export interface DialogueOverlayRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface BattleDialogueOverlayLayout {
  readonly button: { readonly x: number; readonly y: number };
  readonly buttonIcon: { readonly x: number; readonly y: number };
  readonly buttonStem: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
  readonly buttonWaves: readonly {
    readonly cx: number;
    readonly cy: number;
    readonly radius: number;
  }[];
  readonly ping: { readonly x: number; readonly y: number };
  readonly panel: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
  readonly headerBar: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
  readonly leds: readonly { readonly x: number; readonly y: number }[];
  readonly projectionBeams: readonly {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly rotation: number;
  }[];
  readonly scanlines: readonly {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  }[];
  readonly cornerBrackets: readonly {
    readonly x: number;
    readonly y: number;
    readonly size: number;
    readonly flipX: boolean;
    readonly flipY: boolean;
  }[];
  readonly portrait: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
  readonly portraitInitial: { readonly x: number; readonly y: number };
  readonly portraitRing: {
    readonly x: number;
    readonly y: number;
    readonly radius: number;
  };
  readonly portraitWaveBars: readonly {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  }[];
  readonly signalWaves: readonly {
    readonly cx: number;
    readonly cy: number;
    readonly radius: number;
  }[];
  readonly speaker: { readonly x: number; readonly y: number };
  readonly body: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
  };
  readonly previousButton: { readonly x: number; readonly y: number };
  readonly nextButton: { readonly x: number; readonly y: number };
  readonly closeButton: { readonly x: number; readonly y: number };
}

const BUTTON_W = 72;
const BUTTON_H = 18;
const PANEL_H = 96;
const PANEL_PAD = 8;
const PORTRAIT_SIZE = 42;
const PORTRAIT_GAP = 22;
const EDGE_PAD = 8;
const HEADER_H = 10;
const LED_COUNT = 3;
const SIGNAL_WAVE_COUNT = 3;
const CORNER_SIZE = 6;
const SCANLINE_COUNT = 4;
const PROJECTION_BEAM_COUNT = 3;
const ACTION_BUTTON_W = 28;
const ACTION_BUTTON_H = 18;
const ACTION_BUTTON_GAP = 18;
const CLOSE_BUTTON_SIZE = 18;

export function resolveBattleDialogueOverlayLayout(
  rect: DialogueOverlayRect
): BattleDialogueOverlayLayout {
  const right = rect.x + rect.width;
  const buttonX = right - BUTTON_W / 2 - EDGE_PAD;
  const buttonY = rect.y + BUTTON_H / 2 + EDGE_PAD;
  const panelW = Math.max(1, rect.width - EDGE_PAD * 2);
  const panelX = rect.x + Math.max(EDGE_PAD, (rect.width - panelW) / 2);
  const availablePanelH = Math.max(1, rect.height - EDGE_PAD * 2);
  const panelH = Math.min(PANEL_H, availablePanelH);
  const panelY = rect.y + EDGE_PAD + Math.max(0, availablePanelH - panelH);
  const portraitSize = Math.max(
    28,
    Math.min(PORTRAIT_SIZE, panelH - PANEL_PAD * 2 - HEADER_H - 2)
  );
  const portraitX = panelX + PANEL_PAD;
  const portraitY = panelY + PANEL_PAD + HEADER_H + 2;
  const textX = portraitX + portraitSize + PORTRAIT_GAP;
  const textRight = panelX + panelW - PANEL_PAD;
  const bodyW = Math.max(1, textRight - textX);
  const actionRight = panelX + panelW - PANEL_PAD;
  const actionTotalW = ACTION_BUTTON_W * 2 + ACTION_BUTTON_GAP;
  const actionLeft = Math.max(panelX + PANEL_PAD, actionRight - actionTotalW);
  const actionY = panelY + panelH - 15;
  const closeX = panelX + panelW - PANEL_PAD - CLOSE_BUTTON_SIZE / 2;
  const closeY = panelY + PANEL_PAD + CLOSE_BUTTON_SIZE / 2;
  const bodyY = panelY + PANEL_PAD + HEADER_H + 16;
  const bodyH = Math.max(1, actionY - ACTION_BUTTON_H / 2 - 4 - bodyY);
  const buttonIcon = { x: buttonX - BUTTON_W / 2 + 14, y: buttonY };
  const buttonStem = {
    x: buttonIcon.x - 1,
    y: buttonIcon.y + 4,
    width: 2,
    height: 5,
  };
  const buttonWaves = [
    { cx: buttonIcon.x + 4, cy: buttonIcon.y, radius: 7 },
    { cx: buttonIcon.x + 4, cy: buttonIcon.y, radius: 11 },
  ];

  const headerBar = {
    x: panelX,
    y: panelY,
    width: panelW,
    height: HEADER_H,
  };

  const leds: { x: number; y: number }[] = [];
  for (let i = 0; i < LED_COUNT; i++) {
    leds.push({
      x: panelX + panelW * (0.18 + i * 0.32),
      y: panelY + HEADER_H / 2,
    });
  }

  const projectionBeams: {
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
  }[] = [];
  for (let i = 0; i < PROJECTION_BEAM_COUNT; i++) {
    projectionBeams.push({
      x: panelX + PANEL_PAD + i * Math.max(24, panelW * 0.22),
      y: panelY + HEADER_H + 14 + i * 17,
      width: Math.max(34, panelW * 0.34),
      height: 1,
      rotation: -0.18,
    });
  }

  const scanlineRegion = {
    x: panelX + PANEL_PAD,
    y: panelY + HEADER_H + 10,
    width: panelW - PANEL_PAD * 2,
    height: panelH - HEADER_H - 22,
  };
  const scanlines: { x: number; y: number; width: number; height: number }[] =
    [];
  for (let i = 0; i < SCANLINE_COUNT; i++) {
    scanlines.push({
      x: scanlineRegion.x,
      y: scanlineRegion.y + i * 14,
      width: scanlineRegion.width,
      height: 1,
    });
  }

  const waveCx = portraitX + portraitSize;
  const waveCy = portraitY + portraitSize / 2;
  const signalWaves: { cx: number; cy: number; radius: number }[] = [];
  for (let i = 0; i < SIGNAL_WAVE_COUNT; i++) {
    signalWaves.push({
      cx: waveCx,
      cy: waveCy,
      radius: Math.min(6 + i * 5, Math.max(4, portraitSize / 2 + i * 2)),
    });
  }

  const portraitRing = {
    x: portraitX + portraitSize / 2,
    y: portraitY + portraitSize / 2,
    radius: Math.max(8, portraitSize / 2 - 5),
  };
  const portraitWaveBars = [
    {
      x: portraitX + 7,
      y: portraitY + portraitSize - 9,
      width: Math.max(12, portraitSize - 14),
      height: 1,
    },
    {
      x: portraitX + 11,
      y: portraitY + portraitSize - 6,
      width: Math.max(8, portraitSize - 22),
      height: 1,
    },
    {
      x: portraitX + 15,
      y: portraitY + portraitSize - 3,
      width: Math.max(4, portraitSize - 30),
      height: 1,
    },
  ];

  const cornerBrackets = [
    {
      x: panelX + 2,
      y: panelY + HEADER_H + 2,
      size: CORNER_SIZE,
      flipX: false,
      flipY: false,
    },
    {
      x: panelX + panelW - 2,
      y: panelY + panelH - 2,
      size: CORNER_SIZE,
      flipX: true,
      flipY: true,
    },
  ];

  return {
    button: { x: buttonX, y: buttonY },
    buttonIcon,
    buttonStem,
    buttonWaves,
    ping: { x: buttonX + BUTTON_W / 2 - 5, y: buttonY - BUTTON_H / 2 + 4 },
    panel: { x: panelX, y: panelY, width: panelW, height: panelH },
    headerBar,
    leds,
    projectionBeams,
    scanlines,
    cornerBrackets,
    portrait: {
      x: portraitX,
      y: portraitY,
      width: portraitSize,
      height: portraitSize,
    },
    portraitInitial: {
      x: portraitX + portraitSize / 2,
      y: portraitY + portraitSize / 2 + 1,
    },
    portraitRing,
    portraitWaveBars,
    signalWaves,
    speaker: { x: textX, y: panelY + PANEL_PAD + HEADER_H },
    body: {
      x: textX,
      y: bodyY,
      width: bodyW,
      height: bodyH,
    },
    previousButton: {
      x: actionLeft + ACTION_BUTTON_W / 2,
      y: actionY,
    },
    nextButton: {
      x: actionLeft + ACTION_BUTTON_W + ACTION_BUTTON_GAP + ACTION_BUTTON_W / 2,
      y: actionY,
    },
    closeButton: { x: closeX, y: closeY },
  };
}
